import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper,
  Button,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { CameraAlt, Upload, Logout } from '@mui/icons-material';
import PassportScanner from '../components/PassportScanner';
import { useAuth } from '../auth/useAuth';

function TaxScannerPage() {
  const { user, logout, api } = useAuth();
  const [isCameraScannerOpen, setIsCameraScannerOpen] = useState(false);
  const [isFileScannerOpen, setIsFileScannerOpen] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [scanHistory, setScanHistory] = useState([]);

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const handleScanComplete = async (scannedPassportData) => {
    console.log('📄 Passport scanned:', scannedPassportData);
    setScannedData(scannedPassportData);
    
    // הוספה להיסטוריה
    const scanEntry = {
      id: Date.now(),
      timestamp: new Date(),
      data: scannedPassportData
    };
    setScanHistory(prev => [scanEntry, ...prev.slice(0, 9)]); // שמירת 10 סריקות אחרונות
    
    showNotification('דרכון נסרק בהצלחה!', 'success');
    
    // שליחה אוטומטית לשרת (אם נדרש)
    await submitScannedData(scannedPassportData);
  };

  const submitScannedData = async (data) => {
    if (!data) return;
    
    setIsSubmitting(true);
    try {
      console.log('📤 Submitting scanned data:', data);
      
      // שליחת הנתונים לשרת
      const response = await api.post('/tax/passport-scan', data);
      
      console.log('✅ Server response:', response.data);
      showNotification('נתונים נשלחו בהצלחה למערכת', 'success');
    } catch (error) {
      console.error('❌ Error submitting scanned data:', error);
      showNotification(
        error.response?.data?.message || 'שגיאה בשליחת הנתונים', 
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearScannedData = () => {
    setScannedData(null);
  };

  const clearHistory = () => {
    setScanHistory([]);
    showNotification('היסטוריה נוקתה', 'info');
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#f5f5f5',
      py: 2
    }}>
      <Container maxWidth="md">
        {/* כותרת */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom color="primary">
            🛂 מערכת סריקת דרכונים
          </Typography>
          <Typography variant="body1" color="text.secondary">
            ברוך הבא {user?.firstName} {user?.lastName}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Logout />}
            onClick={logout}
            sx={{ mt: 2 }}
            size="small"
          >
            התנתק
          </Button>
        </Paper>

        {/* כפתורי סריקה */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            בחר שיטת סריקה:
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<CameraAlt />}
              onClick={() => setIsCameraScannerOpen(true)}
              size="large"
              sx={{ flex: 1, minWidth: 200, py: 2 }}
            >
              📷 צלם דרכון
            </Button>
            
            <Button
              variant="contained"
              startIcon={<Upload />}
              onClick={() => setIsFileScannerOpen(true)}
              size="large"
              sx={{ flex: 1, minWidth: 200, py: 2 }}
              color="secondary"
            >
              📁 העלה תמונה
            </Button>
          </Box>
        </Paper>

        {/* תוצאות הסריקה */}
        {scannedData && (
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" color="success.main">
                ✅ נתוני דרכון נסרקו
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={clearScannedData}
              >
                נקה
              </Button>
            </Box>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
              {scannedData.firstNameEn && (
                <Box>
                  <Typography variant="body2" color="text.secondary">שם פרטי (אנגלית)</Typography>
                  <Typography variant="body1" fontWeight="bold">{scannedData.firstNameEn}</Typography>
                </Box>
              )}
              
              {scannedData.lastNameEn && (
                <Box>
                  <Typography variant="body2" color="text.secondary">שם משפחה (אנגלית)</Typography>
                  <Typography variant="body1" fontWeight="bold">{scannedData.lastNameEn}</Typography>
                </Box>
              )}
              
              {scannedData.passportNumber && (
                <Box>
                  <Typography variant="body2" color="text.secondary">מספר דרכון</Typography>
                  <Typography variant="body1" fontWeight="bold">{scannedData.passportNumber}</Typography>
                </Box>
              )}
              
              {scannedData.idNumber && (
                <Box>
                  <Typography variant="body2" color="text.secondary">מספר זהות</Typography>
                  <Typography variant="body1" fontWeight="bold">{scannedData.idNumber}</Typography>
                </Box>
              )}
              
              {scannedData.birthDate && (
                <Box>
                  <Typography variant="body2" color="text.secondary">תאריך לידה</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {new Date(scannedData.birthDate).toLocaleDateString('he-IL')}
                  </Typography>
                </Box>
              )}
              
              {scannedData.citizenship && (
                <Box>
                  <Typography variant="body2" color="text.secondary">אזרחות</Typography>
                  <Typography variant="body1" fontWeight="bold">{scannedData.citizenship}</Typography>
                </Box>
              )}
            </Box>
            
            {isSubmitting && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  שולח נתונים למערכת...
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* היסטוריה */}
        {scanHistory.length > 0 && (
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                📋 היסטוריית סריקות ({scanHistory.length})
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={clearHistory}
                color="warning"
              >
                נקה היסטוריה
              </Button>
            </Box>
            
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {scanHistory.map((scan) => (
                <Box 
                  key={scan.id} 
                  sx={{ 
                    p: 2, 
                    mb: 1, 
                    bgcolor: 'grey.50', 
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}
                >
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {scan.timestamp.toLocaleString('he-IL')}
                  </Typography>
                  <Typography variant="body2">
                    {scan.data.firstNameEn} {scan.data.lastNameEn} | 
                    {scan.data.passportNumber ? ` דרכון: ${scan.data.passportNumber}` : ''} |
                    {scan.data.idNumber ? ` ת.ז: ${scan.data.idNumber}` : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        {/* סורקי הדרכון */}
        <PassportScanner
          open={isCameraScannerOpen}
          onClose={() => setIsCameraScannerOpen(false)}
          onScanComplete={handleScanComplete}
          mode="camera"
        />
        
        <PassportScanner
          open={isFileScannerOpen}
          onClose={() => setIsFileScannerOpen(false)}
          onScanComplete={handleScanComplete}
          mode="file"
        />

        {/* התרעות */}
        <Snackbar
          open={notification.open}
          autoHideDuration={4000}
          onClose={() => setNotification({ ...notification, open: false })}
        >
          <Alert 
            onClose={() => setNotification({ ...notification, open: false })} 
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}

export default TaxScannerPage;
